// Pure helpers for the Companion's notion of "what are we talking about".
// No React, no fetch: unit-tested with node --test.

// What kind of thing the user is looking at. Entry wins over page wins over set
// wins over opening, because a selected copy is the most specific target.
export function contextKind(ctx) {
  if (!ctx) return 'general';
  if (ctx.entry_id !== undefined && ctx.entry_id !== null) return 'entry';
  if (ctx.compartment_id !== undefined && ctx.compartment_id !== null) return 'page';
  if (ctx.location_id !== undefined && ctx.location_id !== null) return 'page';
  if (ctx.set_id) return 'set';
  if (ctx.opening_id) return 'opening';
  return 'general';
}

// Suggested prompts per context kind. These are translation keys; the sheet
// renders them with t() and sends the rendered English-or-localized text as the
// message. Nothing here is sent until the user taps it.
const SUGGESTIONS = {
  entry: ['companion.suggest.entry.why', 'companion.suggest.entry.others', 'companion.suggest.entry.finish'],
  page: ['companion.suggest.page.whatIsHere', 'companion.suggest.page.missing', 'companion.suggest.page.duplicates'],
  set: ['companion.suggest.set.missing', 'companion.suggest.set.gems', 'companion.suggest.set.complete'],
  opening: ['companion.suggest.opening.new', 'companion.suggest.opening.twice', 'companion.suggest.opening.story'],
  general: ['companion.suggest.general.gems', 'companion.suggest.general.quiz', 'companion.suggest.general.recent']
};

export function suggestionKeys(kind) {
  return SUGGESTIONS[kind] || SUGGESTIONS.general;
}

// A short human title for the sheet header, built from whatever metadata the
// opening screen could supply. Falls back gracefully so the header never shows
// a raw id.
export function contextTitle(ctx, meta = {}) {
  const kind = contextKind(ctx);
  switch (kind) {
    case 'entry': {
      const parts = [meta.name || meta.title || ''];
      const finish = meta.variantLabel || meta.printing;
      if (finish && finish !== 'Normal') parts.push(finish);
      if (meta.number) parts.push(`#${meta.number}`);
      return parts.filter(Boolean).join(' · ') || 'This copy';
    }
    case 'page': {
      const bits = [];
      if (meta.locationName) bits.push(meta.locationName);
      if (ctx.page) bits.push(`page ${ctx.page}`);
      else if (meta.compartmentLabel) bits.push(meta.compartmentLabel);
      return bits.join(' · ') || 'This page';
    }
    case 'set':
      return meta.setName || ctx.set_id || 'This set';
    case 'opening':
      return meta.openingTitle || 'Tonight\'s opening';
    default:
      return meta.title || 'Your binder';
  }
}

// Answer cards come typed from the server; anything else is rendered as a
// generic summary so an unknown type never crashes the sheet.
export const CARD_TYPES = ['entry', 'completion', 'quiz', 'summary'];
export function normalizeCard(card) {
  if (!card || typeof card !== 'object') return { type: 'summary', title: '', data: {} };
  const type = CARD_TYPES.includes(card.type) ? card.type : 'summary';
  return { type, title: card.title || '', data: card.data && typeof card.data === 'object' ? card.data : {} };
}
